import { Button, cn } from '@seen/ui'
import { IconLayoutGrid, IconLayoutList } from '@tabler/icons-react'
import type { JSX } from 'react'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { type DeskView, useDeskViewStore } from '#src/hooks/useDeskViewStore.ts'
import { DeskSearchButton } from '#src/pages/desks/DeskSearchButton.tsx'
import {
  DESK_LIST_HEADER_GAP_CLASSNAME,
  deskPaneHeaderVariants,
} from '#src/pages/desks/deskViewLayout.ts'

/* The canvas/list switcher itself. In list view this renders directly into the
 * pane header as an ordinary flex child beside the search field; on the canvas
 * it goes inside the floating band below, which is the only place it has to be
 * positioned by hand.
 *
 * Styled off the canvas's own outline chrome (ControlsPanel, the top-right
 * add-note panel) rather than a new segmented-control primitive.
 *
 * The design's segmented control: one bordered, rounded container with 6px of
 * padding, and the active side carried by a filled rounded chip inset inside it
 * rather than by a full-bleed segment split off with a divider. Height stays
 * 40px to match the search field beside it — the frame draws both at 36px, but
 * the two have to agree with each other and with the note header's own 40px
 * pills more than with the frame.
 *
 * `shrink-0` so dragging the list pane narrow squeezes the search field rather
 * than the buttons. */
export function DeskViewToggleGroup({
  deskId,
  view,
  className,
}: {
  deskId: string
  view: DeskView
  className?: string
}): JSX.Element {
  const setDeskView = useDeskViewStore((state) => state.setDeskView)
  return (
    <div
      data-testid="desk-view-toggle-group"
      className={cn(
        'flex h-10 shrink-0 items-center gap-1 rounded-md border bg-background p-1.5',
        className
      )}
    >
      <DeskViewToggleButton
        label="Canvas view"
        isActive={view === 'canvas'}
        onClick={() => setDeskView(deskId, 'canvas')}
      >
        <IconLayoutGrid className="size-4" />
      </DeskViewToggleButton>
      <DeskViewToggleButton
        label="List view"
        isActive={view === 'list'}
        onClick={() => setDeskView(deskId, 'list')}
      >
        <IconLayoutList className="size-4" />
      </DeskViewToggleButton>
    </div>
  )
}

/* The canvas's floating placement of the switcher. Canvas only: the flow pane
 * has no header row to put the group in, so there it is pinned to the page's
 * top-left instead. List view renders the group straight into its pane header.
 *
 * The band is the header-height box, not the button group, so `items-center`
 * lands the buttons at the same offset the list header's own `items-center`
 * gives them — the switcher holds its position across the two views.
 *
 * It carries the search button to the switcher's right, on the same gap the
 * list header puts between its own switcher and search field — so the control
 * that opens search sits where the field it opens will appear. */
export function DeskViewToggle({
  deskId,
  view,
  className,
}: {
  deskId: string
  view: DeskView
  className?: string
}): JSX.Element {
  return (
    <div
      data-testid="desk-view-toggle"
      className={cn(
        deskPaneHeaderVariants({ placement: 'band' }),
        DESK_LIST_HEADER_GAP_CLASSNAME,
        className
      )}
    >
      {/* `pointer-events-auto` because the band disables pointer events on the
       * whole overlay strip; only these controls should intercept clicks. */}
      <DeskViewToggleGroup
        deskId={deskId}
        view={view}
        className="pointer-events-auto"
      />
      {/* Canvas only, which is the one view with no search field of its own.
       * List view puts a real one in this same slot, so a magnifier there would
       * be a second way to reach a box already sitting beside it.
       *
       * The gate is on `view` rather than on the call site because this band is
       * also floated over the loading list view — DeskPage's Suspense fallback —
       * where the field is a moment away and the button would flash. */}
      {view === 'canvas' && (
        <DeskSearchButton deskId={deskId} className="pointer-events-auto" />
      )}
    </div>
  )
}

function DeskViewToggleButton({
  label,
  isActive,
  onClick,
  className,
  children,
}: {
  label: string
  isActive: boolean
  onClick: () => void
  className?: string
  children: JSX.Element
}): JSX.Element {
  return (
    <Tooltip delayDuration={500}>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={label}
          aria-pressed={isActive}
          /* `bg-border` for the active chip, which reads oddly as a class but is
           * the right token: the design fills it with its own Grey-200
           * (#E7E5E4), and Orbit's Grey-200 is `--border` (#E4E2DD). `accent`
           * resolves to #F8F8F7, which against the white container is close to
           * invisible — the active side has to be legible at a glance. */
          className={cn(
            'size-7 rounded hover:bg-muted',
            isActive && 'bg-border hover:bg-border',
            className
          )}
          onClick={onClick}
        >
          {children}
        </Button>
      </TooltipTrigger>
      {/* Below, not beside: to the right the tooltip lands on top of the search
       * field next to the switcher. Both halves render through this component,
       * so the two tooltips can't drift apart. `TooltipContent` already portals
       * to the body, so dropping below the 60px header band is not clipped by
       * the header or the list's scroll container. */}
      <TooltipContent side="bottom">{label}</TooltipContent>
    </Tooltip>
  )
}
